// src/pages/HomeDashboard.jsx
// Landing page post-login — EduTech Pulse Lab
// Bienvenida al laboratorio: Programas + Sparring IA + Actividad reciente

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import {
  BookOpen,
  Brain,
  FlaskConical,
  ArrowRight,
  Clock,
  Zap,
  Target,
  ChevronRight,
} from 'lucide-react';

const HomeDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentActivity, setRecentActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(true);

  const firstName = user?.nombre?.split(' ')[0] || 'bienvenido';

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const res = await apiClient.get('/stats/recent-activity');
        if (res.data?.success) setRecentActivity(res.data.activity);
      } catch (_) {
        // silencioso — actividad es informativa
      } finally {
        setLoadingActivity(false);
      }
    };
    fetchActivity();
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F6FA]">

      {/* ── Hero — Móvil: ultra-compacto (4 líneas max) | Desktop: completo ── */}
      <section className="bg-gradient-to-br from-[#0B1F3A] via-[#0F2D52] to-[#0B3161] px-5 md:px-6 py-6 md:py-9">
        <div className="max-w-[1100px] mx-auto">
          {/* Label laboratorio — solo desktop */}
          <div className="hidden md:flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-xs font-medium text-cyan-300 tracking-widest uppercase">
              Laboratorio activo
            </span>
          </div>

          {/* Saludo */}
          <h1 className="text-xl md:text-4xl font-bold text-white mb-2 md:mb-3 tracking-tight">
            Bienvenido, {firstName}
          </h1>
          
          {/* Copy móvil: 4 líneas máximo */}
          <p className="md:hidden text-sm text-blue-200/90 leading-snug font-light">
            Laboratorio de aprendizaje con IA.
          </p>
          <p className="md:hidden text-sm text-blue-200/70 leading-snug font-light mt-2">
            Aprende a decidir mejor en contextos reales.
          </p>
          
          {/* Copy desktop: directo, sin redundancia */}
          <p className="hidden md:block text-base md:text-lg text-blue-200/80 max-w-2xl leading-relaxed font-light">
            Laboratorio donde practicas decisiones, contrastas tus ideas y mejoras continuamente.
          </p>

          {/* CTAs principales — Móvil: full-width, visibles sin scroll */}
          <div className="flex flex-col sm:flex-row gap-3 mt-5 md:mt-8">
            <button
              onClick={() => navigate('/cursos-grupales')}
              className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-3 bg-white text-[#0B1F3A] px-5 py-3.5 rounded-xl font-semibold text-sm hover:bg-blue-50 transition-all shadow-md group"
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-blue-600" />
                Explorar Programas
              </span>
              <ArrowRight className="w-4 h-4 text-blue-500 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/vertex/lab')}
              className="w-full sm:w-auto relative flex items-center justify-center sm:justify-start gap-3 bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 px-5 py-3.5 rounded-xl font-semibold text-sm hover:bg-cyan-500/30 hover:border-cyan-400/60 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-cyan-300" />
                Sparring IA
              </span>
              <ArrowRight className="w-4 h-4 text-cyan-300" />
            </button>
          </div>
        </div>
      </section>

      {/* ── Contenido principal — Móvil: spacing reducido ── */}
      <div className="max-w-[1100px] mx-auto px-5 md:px-6 py-6 md:py-8 space-y-4 md:space-y-6">

        {/* Tarjetas de acceso rápido */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Card Programas — Móvil: texto reducido | Padding aumentado + Título bold */}
          <button
            onClick={() => navigate('/cursos-grupales')}
            className="group bg-white rounded-2xl border border-gray-200 p-6 md:p-7 text-left hover:border-blue-300 hover:shadow-lg transition-all min-h-[140px] md:min-h-[160px] flex flex-col justify-between active:scale-[0.98]"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all mt-1" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-1.5">Programas</h3>
              
              {/* Móvil: 2 líneas | Desktop: completo */}
              <p className="md:hidden text-sm text-gray-500 leading-snug">
                Aprende con ciclos de práctica, crítica y mejora.
              </p>
              <p className="hidden md:block text-sm text-gray-500 leading-relaxed">
                Experiencias estructuradas de aprendizaje basadas en ciclos de producción, crítica y mejora.
              </p>
              
              <span className="inline-flex items-center gap-1 mt-2 md:mt-3 text-xs font-medium text-blue-600">
                Ver programas <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </button>

          {/* Card Sparring IA */}
          <button
            onClick={() => navigate('/vertex/lab')}
            className="group relative bg-white rounded-2xl border border-gray-200 p-6 md:p-7 text-left min-h-[140px] md:min-h-[160px] flex flex-col justify-between hover:border-cyan-300 hover:shadow-lg transition-all active:scale-[0.98] cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-cyan-50 flex items-center justify-center">
                <Brain className="w-5 h-5 text-cyan-600" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all mt-1" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-1.5">Sparring IA</h3>
              
              {/* Móvil: 2 líneas | Desktop: completo */}
              <p className="md:hidden text-sm text-gray-500 leading-snug">
                Entrena decisiones con coaches de inteligencia artificial.
              </p>
              <p className="hidden md:block text-sm text-gray-500 leading-relaxed">
                Simulaciones y coaches conversacionales para entrenar criterio y argumentación en tiempo real.
              </p>
              
              <span className="inline-flex items-center gap-1 mt-2 md:mt-3 text-xs font-medium text-cyan-600">
                Entrar al laboratorio <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </button>
        </div>

        {/* Actividad reciente — JERARQUÍA REFORZADA: tipo card principal, más alto, más contrastado */}
        {/* Debe sentirse como "este es tu próximo paso" */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 md:p-7 shadow-sm">
          <h3 className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4">
            Continúa donde lo dejaste
          </h3>

          {loadingActivity ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : recentActivity ? (
            <div className="space-y-3">
              {recentActivity.activeProgram && (
                <button
                  onClick={() => navigate(`/course/${recentActivity.activeProgram.sectionId}/workspace`)}
                  className="flex items-center justify-between w-full px-5 py-5 md:py-4 bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-xl border-2 border-blue-100 hover:border-blue-300 hover:from-blue-100 hover:to-blue-50 active:bg-blue-200 transition-all group shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-base md:text-sm font-bold md:font-semibold text-gray-900">
                        {recentActivity.activeProgram.title}
                      </p>
                      <p className="text-sm md:text-xs text-gray-600 font-medium">Semana {recentActivity.activeProgram.currentWeek}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="hidden md:inline text-sm font-semibold text-blue-600">Continuar</span>
                    <ChevronRight className="w-5 h-5 text-blue-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              )}
              {recentActivity.lastAISession && (
                <div
                  className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 rounded-xl opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center gap-3">
                    <Zap className="w-4 h-4 text-cyan-500" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-800">Última sesión IA</p>
                      <p className="text-xs text-gray-500">{recentActivity.lastAISession.agentName}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-400">Próx.</span>
                </div>
              )}
            </div>
          ) : (
            // Estado vacío — primer acceso
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 rounded-xl border border-gray-100">
                <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700 font-semibold">Sin actividad reciente</p>
                  <p className="text-xs text-gray-500">Comienza explorando los programas disponibles</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/cursos-grupales')}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
              >
                <BookOpen className="w-4 h-4" />
                Ver programas disponibles
              </button>
            </div>
          )}
        </div>

        {/* Principios del laboratorio — Móvil: ultraligera | Desktop: completa */}
        <div className="border border-gray-200 rounded-2xl bg-white/60 px-5 md:px-6 py-4">
          <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-3">
            Cómo funciona el laboratorio
          </p>
          
          {/* Móvil: solo bullets sin descripciones */}
          <ul className="md:hidden space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <span className="w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
              Aprender practicando
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <span className="w-1 h-1 rounded-full bg-cyan-500 flex-shrink-0" />
              IA como acompañante
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <span className="w-1 h-1 rounded-full bg-indigo-500 flex-shrink-0" />
              Evolución continua
            </li>
          </ul>
          
          {/* Desktop: completo con íconos y descripciones */}
          <div className="hidden md:grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
            {[
              { icon: FlaskConical, color: 'text-blue-500', label: 'Aprender practicando', desc: 'El criterio se construye en la acción.' },
              { icon: Brain,        color: 'text-cyan-500',  label: 'IA como acompañante',  desc: 'La IA retroalimenta — el juicio es tuyo.' },
              { icon: Target,       color: 'text-indigo-500', label: 'Evolución continua',   desc: 'Cada ciclo mejora sobre el anterior.' },
            ].map(({ icon: Icon, color, label, desc }) => (
              <div key={label} className="flex items-center gap-3 py-3 sm:py-0 sm:px-5 first:pl-0 last:pr-0">
                <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
                <div>
                  <p className="text-sm font-medium text-gray-700">{label}</p>
                  <p className="text-xs text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeDashboard;
